<script setup lang="ts">
import { IonIcon, IonInput } from '@ionic/vue';
const {
  leftIcon = '',
  rightIcon = '',
  error = false,
  clearInput,
  rightIconStyle = {},
} = defineProps<{
  error?: boolean;
  leftIcon?: string;
  rightIcon?: string;
  clearInput?: boolean;
  rightIconStyle?: Record<string, string | number>;
}>();

defineEmits(['on-right-icon-click']);
</script>

<template>
  <div class="base-input-wrapper" :class="{ 'with-left-icon': !!leftIcon, 'with-right-icon': !!rightIcon }">
    <ion-icon v-if="leftIcon" :icon="leftIcon" class="left-icon absolute text-2xl text-white" />
    <ion-input
      v-bind="$attrs"
      class="base-input"
      :class="{ 'ion-invalid': error }"
      fill="outline"
      label-placement="floating"
      :clear-input="clearInput"
      mode="md"
    />

    <ion-icon
      v-if="rightIcon"
      :icon="rightIcon"
      @click="$emit('on-right-icon-click')"
      class="right-icon absolute text-2xl text-white"
      :style="rightIconStyle"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/scss/setup/_variables.scss';
@import '@/scss/setup/_functions.scss';

//Must use CSS vars for ionic components
.base-input-wrapper {
  position: relative;

  &.with-left-icon {
    .left-icon {
      top: 50%;
      left: toRem(12px);
      z-index: 1000;
      transform: translateY(-50%);
    }

    ion-input {
      --padding-start: 46px !important;
    }
  }

  ion-input {
    --background: var(--ion-color-dark);
    --border-radius: 8px;
    --padding-start: 12px;
    --padding-end: 12px;
    --color: var(--ion-color-light-medium);

    min-height: toRem(50px);
    font-size: toRem(16px);

    :deep(input:-internal-autofill-selected) {
      -webkit-background-clip: text !important;
      background-clip: text !important;
    }

    :deep(input:-internal-autofill-selected::first-line) {
      color: var(--ion-color-tertiary);
    }

    &:not(.label-floating) :deep(.label-text-wrapper) {
      margin-top: toRem(-8px);
      color: var(--ion-color-medium-light);
    }

    &.label-floating :deep(.label-text-wrapper) {
      color: var(--ion-color-white) !important;
    }

    &.has-value:not(.has-focus) :deep(.input-outline-container.sc-ion-input-md) {
      --border-color: var(--ion-color-tertiary);
    }

    &:not(.has-focus):not(.has-value) :deep(.input-outline-container.sc-ion-input-md) {
      --border-color: var(--ion-color-light-medium);
    }

    :deep(.input-clear-icon.sc-ion-input-md) {
      color: inherit;
    }

    &.input-disabled {
      opacity: 1;
      --color: var(--ion-color-medium-light) !important;

      :deep(.input-outline-container.sc-ion-input-md),
      :deep(.input-outline-container.sc-ion-input-ios) {
        --border-color: var(--ion-color-medium-light) !important;
      }

      &.label-floating :deep(.label-text-wrapper) {
        color: var(--ion-color-medium-light) !important;
      }
    }

    &.ion-invalid {
      :deep(.input-outline-container.sc-ion-input-md),
      :deep(.input-outline-container.sc-ion-input-ios) {
        --border-color: var(--ion-color-danger) !important;
      }

      :deep(.input-bottom.sc-ion-input-md),
      :deep(.input-bottom.sc-ion-input-ios) {
        justify-content: start;
      }

      :deep(.error-text.sc-ion-input-md),
      :deep(.error-text.sc-ion-input-ios) {
        display: revert;
      }
    }
  }

  &.with-right-icon {
    :deep(.native-input) {
      max-width: calc(100% - toRem(52px)); // Base
    }

    .right-icon {
      padding-inline-start: toRem(17px);

      z-index: 1000;
      right: toRem(15px);
      top: 50%;
      transform: translateY(-50%);

      border-left: 1px solid $color-medium-light;
    }
  }
}
</style>
